/**
 * HistoryList - Lista da aba Histórico agrupada por (contato, canal)
 *
 * Infinite scroll (mesmo mecanismo do ConversationList), consumindo
 * GET /conversations/history via useInfiniteConversationHistory.
 * Filtro: apenas busca (server-side, resolve contatos por nome/telefone/email).
 */

import React, { useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useInfiniteConversationHistory } from '../hooks';
import { HistoryGroupItem } from './HistoryGroupItem';
import { Icon } from '@/shared/icons';

interface HistoryListProps {
  /** Callback ao selecionar um grupo (contato+canal) */
  onSelectGroup?: (contactId: string, channelId: string) => void;
  /** Se a sidebar está expandida */
  expanded?: boolean;
  /** Query de busca (server-side) */
  searchQuery?: string;
  /** Habilitar fetch (condicionar à aba ativa) */
  enabled?: boolean;
}

export const HistoryList: React.FC<HistoryListProps> = ({
  onSelectGroup,
  expanded = true,
  searchQuery = '',
  enabled = true,
}) => {
  // Na rota /history/:contactId/:channelId os params destacam o par aberto;
  // em /chat os params não existem → sem highlight
  const { contactId: selectedContactId, channelId: selectedChannelId } =
    useParams<{ contactId?: string; channelId?: string }>();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteConversationHistory({
    search: searchQuery,
    limit: 25,
    enabled,
  });

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.5, rootMargin: '100px' }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const groups = data?.pages.flatMap((page) => page.items) ?? [];

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4" data-testid="history-list-loading">
        <div className="flex flex-col items-center gap-2">
          <Icon name="loader" className="h-6 w-6 animate-spin text-primary-500" />
          <p className="text-sm text-gray-500">Carregando histórico...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1 flex items-center justify-center p-4" data-testid="history-list-error">
        <div className="text-center">
          <p className="text-sm text-red-500">Erro ao carregar histórico</p>
          <p className="text-xs text-gray-500 mt-1">
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
        </div>
      </div>
    );
  }

  if (groups.length === 0) {
    if (!expanded) {
      return <div className="flex-1"></div>;
    }
    return (
      <div className="flex-1 flex items-center justify-center p-4" data-testid="history-list-empty">
        <div className="text-center">
          <p className="text-sm text-gray-500">
            {searchQuery ? 'Nenhum histórico encontrado para a busca' : 'Nenhum atendimento finalizado'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {searchQuery ? 'Tente outro termo' : 'Conversas fechadas aparecem aqui, agrupadas por contato e canal'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto" data-testid="history-list">
      {groups.map((group) => (
        <HistoryGroupItem
          key={`${group.contactId}-${group.channelId}`}
          group={group}
          expanded={expanded}
          isSelected={!!group.contactId && group.contactId === selectedContactId && group.channelId === selectedChannelId}
          onClick={() => {
            if (group.contactId) {
              onSelectGroup?.(group.contactId, group.channelId);
            }
          }}
        />
      ))}

      {hasNextPage && (
        <div ref={loadMoreRef} className="py-4 flex items-center justify-center">
          {isFetchingNextPage ? (
            <div className="flex items-center gap-2 text-primary-500">
              <Icon name="loader" className="h-4 w-4 animate-spin" />
              <span className="text-sm">Carregando mais...</span>
            </div>
          ) : (
            <span className="text-sm text-gray-400">Role para carregar mais</span>
          )}
        </div>
      )}

      {expanded && !hasNextPage && groups.length > 0 && (
        <div className="py-4 text-center">
          <span className="text-xs text-gray-400">
            Todo o histórico carregado ({groups.length})
          </span>
        </div>
      )}
    </div>
  );
};
